'use client'
import { HeyaGreeting, type HeyaHeroCopy } from '@/src/components/common/home/common/heyaHero/heyaGreeting'
import {
    HEYA_HERO_BLEED,
    HeyaHeroBackground
} from '@/src/components/common/home/common/heyaHero/heyaHeroBackground'
import FeatureRenderer from '@/src/components/common/featureRenderer'
import {
    HOME_HERO_GRADIENT_DESKTOP,
    HOME_HERO_GRADIENT_MOBILE
} from '@/src/components/common/home/common/homeHeroBackground'
import ExploreMoreHotelsWrapper from '@/src/components/common/home/exploreMoreHotels'
import HomePromoCardsWrapper from '@/src/components/common/home/homePromoCards'
import HomepageCarousel from '@/src/components/common/home/homepageCarousel'
import HotelShowcaseWrapper from '@/src/components/common/home/hotelShowcase'
import LobTabs from '@/src/components/common/home/hotelSearch/lobTabs'
import HotelSearchForm from '@/src/components/common/home/hotelSearch/searchForm'
import PopularHotelsWrapper from '@/src/components/common/home/popularHotels'
import TrendingHotelsWrapper from '@/src/components/common/home/trendingHotels'
import { useHotelLastSearchInitialValues } from '@/src/hooks/hotels/useHotelLastSearch'
import { Offer } from '@/src/types/offers'
import { Box, Container, Typography } from '@mui/material'

export interface HotelsHomeDesktopProps {
    title: string
    subtitle: string
    tagline: string
    offers: Offer[]
    locale: string
    showVisaTab: boolean
    showFlightsHotelsTab: boolean
    /** Present only on the Heya sub-brand, which replaces the gradient band with a photo hero. */
    heyaCopy?: HeyaHeroCopy
}

// Desktop hotels landing — a branded hero band with the heading and the search form
// in an elevated card, followed by the offers carousel and trending destinations. The
// form owns its state and navigates to /hotels/results.
const HotelsHomeDesktop: React.FC<HotelsHomeDesktopProps> = ({
    title,
    subtitle,
    tagline,
    offers,
    locale,
    showVisaTab,
    showFlightsHotelsTab,
    heyaCopy
}) => {
    // localStorage-backed prefill; `ready` remounts the form after mount so SSR stays empty.
    const { initialValues, ready } = useHotelLastSearchInitialValues()


    const searchCard = (
        <Container maxWidth="xl" sx={{ px: 2 }}>
            <Box sx={{ mt: heyaCopy ? 5 : 3, position: 'relative', zIndex: 2 }}>
                <LobTabs showVisaTab={showVisaTab} showFlightsHotelsTab={showFlightsHotelsTab} />
            </Box>
            <Box
                sx={{
                    mt: '-12px',
                    position: 'relative',
                    zIndex: 1,
                    ...(heyaCopy
                        ? { bgcolor: 'neutral.white', borderRadius: 2, p: 2, pt: 3, boxShadow: '0px 2px 8px -1px rgba(15, 17, 23, 0.1)' }
                        : { bgcolor: 'common.white', px: 3, pt: 4, pb: 3, borderRadius: 2, boxShadow: '0px 2px 8px -1px rgba(15, 17, 23, 0.1)' })
                }}
            >
                <HotelSearchForm key={ready ? 'seeded' : 'empty'} isMobile={false} initialValues={initialValues} />
            </Box>
        </Container>
    )

    return (
        <Box sx={{ bgcolor: 'neutral.100', minHeight: 'calc(100vh - 54px)' }}>
            {heyaCopy ? (
                <>
                    {/* Heya: full-width photo band. The band wraps the copy only, so the photo's
                    height follows the copy rather than the search card. */}
                    <Box sx={{ position: 'relative' }}>
                        <HeyaHeroBackground
                            variant="hotels"
                            display={{ xs: 'none', lg: 'block' }}
                            bleed={HEYA_HERO_BLEED.hotels}
                        />
                        {/* Same Container declaration as the search card below, so the copy sits
                        on the page rail at every width instead of on a gutter of its own. The
                        photo stays full-bleed: it is absolute against the band's padding box. */}
                        <Container maxWidth="xl" sx={{ position: 'relative', px: 2 }}>
                            <HeyaGreeting
                                variant="hotels"
                                badge={heyaCopy.badge}
                                headingLead={heyaCopy.headingLead}
                                headingAccent={heyaCopy.headingAccent}
                                intro={heyaCopy.intro}
                            />
                        </Container>
                    </Box>
                    {searchCard}
                </>
            ) : (
                <Box
                    sx={{
                        background: { xs: HOME_HERO_GRADIENT_MOBILE, lg: HOME_HERO_GRADIENT_DESKTOP },
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'top center',
                        pb: 2
                    }}
                >
                    <Container maxWidth="xl" sx={{ textAlign: 'center', px: 2, pt: { xs: 1.5, lg: 6 }, pb: { xs: 1.5, lg: 0 } }}>
                        <Typography
                            component="h1"
                            variant="h3"
                            sx={{ mb: { xs: 1.5, md: 2 }, fontWeight: 600, fontSize: { xs: '1.75rem', lg: '3.75rem' }, color: 'text.primary' }}
                        >
                            {title}
                        </Typography>
                        {/* Adventure tagline — flights shows this on desktop only. */}
                        <Typography
                            component="h2"
                            variant="h4"
                            sx={{ mb: { md: 2 }, display: { xs: 'none', md: 'block' }, fontWeight: 300, fontSize: 20, color: 'text.primary' }}
                        >
                            {tagline}
                        </Typography>
                        <Typography
                            component="h3"
                            variant="h4"
                            sx={{ mb: { md: 2 }, display: { xs: 'none', md: 'block' }, fontWeight: 300, fontSize: 18, color: 'text.primary' }}
                        >
                            {subtitle}
                        </Typography>
                    </Container>
                    {searchCard}
                </Box>
            )}

            <Box sx={{ mt: 4 }}>
                <HomepageCarousel offers={offers} locale={locale} isMobile={false} />
                <FeatureRenderer type="HOTEL_HOME_SHOWCASE">
                    <HotelShowcaseWrapper />
                </FeatureRenderer>
                <FeatureRenderer type="TRENDING_HOTELS">
                    <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
                        <TrendingHotelsWrapper />
                    </Container>
                </FeatureRenderer>
                <FeatureRenderer type="HOTEL_HOME_SHOWCASE">
                    <PopularHotelsWrapper />
                    <HomePromoCardsWrapper />
                    <ExploreMoreHotelsWrapper />
                </FeatureRenderer>
            </Box>
        </Box>
    )
}

export { HotelsHomeDesktop }
export default HotelsHomeDesktop
